/**
 * Browsing screens: words, grammar, listening, the library and the workbook
 * (docs/04-screen-specs.md C9–C17, H1–H2).
 *
 * Listening is where the freshness mechanic lives. A learner with nothing due needs a
 * reason to open the app that is not a streak notification, and weekly drops of real
 * speech are that reason.
 */

import { clientUlid, type RouteResponse } from '@saysooner/api-client';
import {
  applySelection,
  buildMatching,
  type Cell,
  type CrosswordGrade,
  crosswordGrades,
  DISPUTE_REASON_LABELS,
  DISPUTE_REASON_ORDER,
  type DisputeReason,
  type GameGrade,
  type GameModalityGrade,
  gamesNotBuiltMessage,
  generateCrossword,
  generateWordSearch,
  gradesFor,
  isComplete,
  isCrosswordComplete,
  isMatchingComplete,
  isSpellingComplete,
  matchingGrades,
  resultOf,
  solvedEntries,
  spellingGrades,
  startCrossword,
  startMatching,
  startSession,
  startSpelling,
  submitSpelling,
  tapGloss,
  tapTerm,
  typeLetter,
} from '@saysooner/core';
import { Banner, Button, EmptyState, ErrorState, Skeleton } from '@saysooner/ui';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { JSX } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { type Track, useApi, useApp, useCourseId } from '../app-context.js';
import { mediaUrl, qk, retryIf } from '../lib/client.js';

type ListeningList = RouteResponse<'listListening'>;
type ListeningItem = RouteResponse<'getListeningItem'>;
type GrammarList = RouteResponse<'listGrammarConcepts'>;
type Item = RouteResponse<'getItem'>;

export interface NavigationProps {
  readonly onNavigate: (route: string) => void;
}

// ── C9 Words hub ────────────────────────────────────────────────────────────

export function WordsScreen({ onNavigate }: NavigationProps): JSX.Element {
  const api = useApi();
  const courseId = useCourseId();
  const { track } = useApp();

  const queue = useQuery({
    queryKey: qk.reviewQueue(courseId, true),
    queryFn: () =>
      api.call('getReviewQueue', { query: { courseId, limit: 200, allowSpoken: true } }),
  });

  // Only two of the four counts are knowable here: the review queue returns what is
  // due, and there is no vocabulary endpoint that reports the whole deck by state.
  // `learning` and `mastered` go as present nulls rather than as a tally of the due
  // subset, which would read as a deck size and be wrong by the size of the deck.
  const cards = queue.data?.cards;
  useEffect(() => {
    if (cards === undefined) return;
    track('words_home_view', {
      new: cards.filter((card) => card.reason === 'new').length,
      learning: null,
      mastered: null,
      due: queue.data?.dueTotal ?? 0,
    });
  }, [cards, queue.data, track]);

  const tiles = [
    ['Your deck', 'Everything you are learning, with its next review', '/learn/words/deck'],
    ['Word search', 'A seeded grid from your own weak words', '/learn/words/games/wordsearch'],
    [
      'Crossword',
      "Clues from your own words — produce them, don't just spot them",
      '/learn/words/games/crossword',
    ],
    ['Matching', 'Pair each word with its meaning', '/learn/words/games/matching'],
    ['Spelling bee', 'Spell it from its meaning, accents forgiven', '/learn/words/games/spelling'],
    [
      'Minimal pairs',
      'Two sounds you keep confusing, side by side',
      '/learn/words/games/minimalpairs',
    ],
    ['Speed match', 'Recognition under time pressure', '/learn/words/games/speedmatch'],
  ] as const;

  return (
    <main id="main" className="vl-page">
      <h1>Words</h1>
      <p className="vl-page__lede">
        Games here draw from your own weak items, not a generic list — so playing one is practice
        rather than a break from it.
      </p>
      <ul className="vl-tile-grid">
        {tiles.map(([title, detail, route]) => (
          <li key={route}>
            <button
              type="button"
              className="vl-tile"
              onClick={() => {
                // The games are the thing worth measuring here: their whole claim is
                // that they draw from the learner's own weak items, and a game nobody
                // starts cannot be making good on that.
                const game = route.split('/').at(-1) ?? route;
                if (route.includes('/games/')) {
                  // The seed is assigned by the game screen itself; the start event
                  // from the hub records which game was chosen.
                  track('word_game_start', { type: game, seed: null });
                }
                onNavigate(route);
              }}
            >
              <span className="vl-tile__title">{title}</span>
              <span className="vl-tile__detail">{detail}</span>
            </button>
          </li>
        ))}
      </ul>
    </main>
  );
}

// ── C10 Word deck ───────────────────────────────────────────────────────────

type DeckFilter = 'all' | 'learning' | 'mastered' | 'leeches';

export function WordDeckScreen({ onNavigate }: NavigationProps): JSX.Element {
  const api = useApi();
  const courseId = useCourseId();
  const { track } = useApp();
  const [filter, setFilter] = useState<DeckFilter>('all');

  const query = useQuery({
    queryKey: qk.reviewQueue(courseId, true),
    queryFn: () =>
      api.call('getReviewQueue', { query: { courseId, limit: 200, allowSpoken: true } }),
  });

  const [view, setView] = useState<'stack' | 'list'>('stack');

  const filters: readonly [DeckFilter, string][] = [
    ['all', 'All'],
    ['learning', 'Learning'],
    ['mastered', 'Mastered'],
    // Named rather than hidden: a learner deserves to know which items keep failing.
    ['leeches', 'Keep slipping'],
  ];

  return (
    <main id="main" className="vl-page">
      <h1>Your deck</h1>

      {/* The stack is the deck, and the list is for going looking. Both, because
          browsing a deck and drilling one are different jobs. */}
      <nav className="vl-chip-filters" aria-label="How to view your deck">
        {(['stack', 'list'] as const).map((option) => (
          <button
            key={option}
            type="button"
            className="vl-chip-filter"
            aria-pressed={view === option}
            onClick={() => setView(option)}
          >
            {option === 'stack' ? 'Swipe' : 'Browse'}
          </button>
        ))}
      </nav>

      {view === 'stack' && query.data !== undefined && (
        <SwipeDeck
          cards={query.data.cards.map((card) => ({
            itemId: card.itemId,
            prompt: card.prompt,
            answer: card.answer,
          }))}
          onComplete={() => undefined}
          track={track}
        />
      )}

      {view === 'list' && (
        <>
          <nav className="vl-chip-filters" aria-label="Filter your deck">
            {filters.map(([id, label]) => (
              <button
                key={id}
                type="button"
                className="vl-chip-filter"
                aria-pressed={filter === id}
                onClick={() => setFilter(id)}
              >
                {label}
              </button>
            ))}
          </nav>

          {query.isPending && <Skeleton lines={6} label="Loading your deck" />}
          {query.error !== null && (
            <ErrorState
              title="We couldn't load your deck"
              {...retryIf(query.error, () => void query.refetch())}
            />
          )}

          {query.data !== undefined &&
            (() => {
              const cards = query.data.cards.filter((card) =>
                filter === 'all'
                  ? true
                  : filter === 'leeches'
                    ? card.reason === 'leech'
                    : filter === 'learning'
                      ? card.reason !== 'leech'
                      : card.reason === 'due',
              );

              if (cards.length === 0) {
                return (
                  <EmptyState
                    title="Nothing in here yet"
                    body="Finish a lesson and the words you met will appear."
                    action={{ label: 'Open your path', onClick: () => onNavigate('/learn/path') }}
                  />
                );
              }

              return (
                <>
                  <p className="vl-deck__count">
                    {cards.length} {cards.length === 1 ? 'item' : 'items'} · {query.data.dueTotal}{' '}
                    due now
                  </p>
                  <ul className="vl-deck">
                    {cards.map((card) => (
                      <li key={card.cardId}>
                        <button
                          type="button"
                          className="vl-deck__row"
                          onClick={() => onNavigate(`/learn/words/${card.itemId}`)}
                        >
                          <span className="vl-deck__prompt">{card.prompt}</span>
                          <span className="vl-deck__answer">{card.answer}</span>
                          {card.reason === 'leech' && (
                            <span className="vl-deck__tag">Keeps slipping</span>
                          )}
                          {card.reason === 'mistake' && (
                            <span className="vl-deck__tag">From a mistake</span>
                          )}
                        </button>
                      </li>
                    ))}
                  </ul>
                </>
              );
            })()}
        </>
      )}
    </main>
  );
}

// ── C11 Word detail ─────────────────────────────────────────────────────────

export function WordDetailScreen({
  itemId,
  onNavigate,
}: NavigationProps & { readonly itemId: string }): JSX.Element {
  const api = useApi();
  const { track } = useApp();
  const query = useQuery({
    queryKey: ['item', itemId] as const,
    queryFn: () => api.call('getItem', { params: { itemId } }),
  });

  // `source` is how the learner arrived: a word opened from the deck is a deliberate
  // look-up, one opened from a mistake is remediation, and the two say different things
  // about whether the deck is worth browsing.
  useEffect(() => {
    track('word_detail_view', { item: itemId, source: 'deck' });
  }, [itemId, track]);

  if (query.isPending) return <Skeleton lines={5} label="Loading this word" />;
  if (query.error !== null || query.data === undefined) {
    return (
      <ErrorState
        title="We couldn't load this word"
        {...retryIf(query.error, () => void query.refetch())}
        alternative={{ label: 'Back to your deck', onClick: () => onNavigate('/learn/words/deck') }}
      />
    );
  }

  const { item, card } = query.data as Item;

  return (
    <main id="main" className="vl-page">
      <h1 lang={item.language}>{item.display}</h1>

      {/* Respelling first: most learners cannot read IPA, and leading with it makes a
          word look like a linguistics problem rather than something to say. */}
      {item.respelling !== null && <p className="vl-word__respelling">{item.respelling}</p>}
      {item.ipa !== null && <p className="vl-word__ipa">/{item.ipa}/</p>}

      <ol className="vl-senses">
        {item.senses.map((sense) => (
          <li key={sense.gloss}>
            {sense.gloss}
            {sense.pos !== null && <span className="vl-senses__pos"> · {sense.pos}</span>}
          </li>
        ))}
      </ol>

      {item.examples.length > 0 && (
        <section>
          <h2>In use</h2>
          <ul className="vl-examples">
            {item.examples.map((example) => (
              <li key={example.text}>
                <span lang={item.language}>{example.text}</span>
                <span className="vl-examples__translation">{example.translation}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Wedge W3, with the actual words in it.
          This said "other regions may say it differently" — the hand-wave you write
          when you have no data. `dialectVariants` was a contract field the publisher
          filled with `[]` and no client read; it is derived from the corpora now, so
          the screen can name the word instead of alluding to it. */}
      {item.dialectVariants.length > 0 && (
        <section className="vl-word__variants">
          <h2>Elsewhere</h2>
          <ul className="vl-variants">
            {item.dialectVariants.map((variant) => (
              <li key={variant.dialect}>
                <span lang={item.language} className="vl-variants__display">
                  {variant.display}
                </span>
                <span className="vl-variants__note">{variant.note ?? variant.dialect}</span>
              </li>
            ))}
          </ul>
          <p className="vl-note">
            Your scoring follows the dialect you chose, so saying it the other way is not marked
            wrong — it is just not what we teach you here.
          </p>
        </section>
      )}

      {item.dialectVariants.length === 0 && item.dialect !== null && (
        <p className="vl-note">
          This is the {item.dialect} form, and the regions we teach say it the same way.
        </p>
      )}

      {/* The learner's own schedule for this word, stated rather than hidden: knowing
          when it is next due is what makes the spacing feel deliberate. */}
      {card !== null && (
        <p className="vl-note">
          Next review {new Date(card.dueAt).toLocaleDateString()} · seen {card.reps}{' '}
          {card.reps === 1 ? 'time' : 'times'}
        </p>
      )}

      <Button
        onClick={() => {
          track('word_practise_tapped', {});
          onNavigate('/review/session');
        }}
      >
        Practise this now
      </Button>

      {/* The word card is where Verbi puts its report flow, and it is the right place:
          a learner notices a wrong gloss while reading the entry, not while being
          drilled on it. The dialect reason matters most here, directly under the
          "Elsewhere" list that makes the claim. */}
      <ReportItemControl itemId={itemId} surface="word_detail" />
    </main>
  );
}

/**
 * "Something's wrong with this word."
 *
 * Shared by every surface that displays a single item, so the six reasons and the
 * wording are the same wherever a learner reaches for them.
 */
export function ReportItemControl({
  itemId,
  surface,
}: {
  readonly itemId: string;
  readonly surface: string;
}): JSX.Element {
  const api = useApi();
  const courseId = useCourseId();
  const { track } = useApp();
  const [open, setOpen] = useState(false);
  const [outcome, setOutcome] = useState<string | null>(null);

  const report = useMutation({
    mutationFn: (reason: DisputeReason) =>
      api.call('reportContent', {
        body: { id: clientUlid(), itemId, courseId, reason, note: null },
      }),
    // The server's own wording, not ours: it names which of the two consequences
    // happened, and they read differently.
    onSuccess: (ack) => setOutcome((ack as { message: string }).message),
  });

  if (outcome !== null) {
    return (
      <p className="vl-note" role="status">
        {outcome}
      </p>
    );
  }

  if (!open) {
    return (
      <Button variant="ghost" size="sm" onClick={() => setOpen(true)}>
        Something&apos;s wrong with this word
      </Button>
    );
  }

  return (
    <section className="vl-report" aria-label="Report a problem with this word">
      <p id="vl-report-word-prompt">What&apos;s wrong with it?</p>
      <ul className="vl-report__reasons" aria-labelledby="vl-report-word-prompt">
        {DISPUTE_REASON_ORDER.map((reason) => (
          <li key={reason}>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                track('content_reported', { item: itemId, reason, surface });
                report.mutate(reason);
              }}
            >
              {DISPUTE_REASON_LABELS[reason]}
            </Button>
          </li>
        ))}
      </ul>
      <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>
        Never mind
      </Button>
    </section>
  );
}

// ── C12 Games ───────────────────────────────────────────────────────────────

export function WordGameScreen({
  gameType,
  onNavigate,
}: NavigationProps & { readonly gameType: string }): JSX.Element {
  const api = useApi();
  const courseId = useCourseId();
  const { track } = useApp();

  const titles: Readonly<Record<string, [string, string]>> = {
    wordsearch: [
      'Word search',
      'A grid seeded from your own weak words — the same seed gives the same grid.',
    ],
    crossword: ['Crossword', 'Clues from your own words. Produce the answer, do not just spot it.'],
    matching: ['Matching', 'Pair each word with its meaning. Both columns are shuffled.'],
    spelling: ['Spelling bee', 'Spell the word from its meaning. Accents are forgiven.'],
    minimalpairs: ['Minimal pairs', 'Two sounds you keep confusing, one after the other.'],
    speedmatch: ['Speed match', 'Recognition under mild time pressure. No penalty for a miss.'],
  };
  const [title, detail] = titles[gameType] ?? ['Word game', 'Practice drawn from your deck.'];

  // The learner's own due cards are where the words come from, which is the whole claim
  // of this screen — a game generated from someone else's word list is a crossword.
  const queue = useQuery({
    queryKey: qk.reviewQueue(courseId, false),
    queryFn: () =>
      api.call('getReviewQueue', { query: { courseId, limit: 40, allowSpoken: false } }),
  });

  if (gameType === 'crossword') {
    if (queue.isPending) return <Skeleton lines={6} label="Building your crossword" />;
    const cards = queue.data?.cards ?? [];
    if (cards.length < 3) {
      return (
        <main id="main" className="vl-page">
          <h1>Crossword</h1>
          <EmptyState
            title="Not enough words yet"
            body="A crossword needs at least three words from your deck. Finish a lesson and come back."
            action={{ label: 'Learn new words', onClick: () => onNavigate('/learn/words/deck') }}
          />
        </main>
      );
    }
    return (
      <CrosswordGame
        cards={cards}
        track={track}
        onFinish={(grades) => {
          if (grades.length > 0) {
            void api.call('submitReviewGrades', {
              body: { courseId, batchId: clientUlid(), grades: [...grades] },
            });
          }
          onNavigate('/learn/words');
        }}
      />
    );
  }

  if (gameType === 'matching' || gameType === 'spelling') {
    if (queue.isPending) return <Skeleton lines={6} label="Dealing your cards" />;
    const cards = queue.data?.cards ?? [];
    if (cards.length < 3) {
      return (
        <main id="main" className="vl-page">
          <h1>{title}</h1>
          <EmptyState
            title="Not enough words yet"
            body="This needs at least three words from your deck. Finish a lesson and come back."
            action={{ label: 'Learn new words', onClick: () => onNavigate('/learn/words/deck') }}
          />
        </main>
      );
    }
    const finish = (grades: readonly GameModalityGrade[]): void => {
      if (grades.length > 0) {
        void api.call('submitReviewGrades', {
          body: { courseId, batchId: clientUlid(), grades: [...grades] },
        });
      }
      onNavigate('/learn/words');
    };
    return gameType === 'matching' ? (
      <MatchingGame cards={cards} track={track} onFinish={finish} />
    ) : (
      <SpellingGame cards={cards} track={track} onFinish={finish} />
    );
  }

  if (gameType !== 'wordsearch') {
    return (
      <main id="main" className="vl-page">
        <h1>{title}</h1>
        <p className="vl-page__lede">{detail}</p>
        {/* Named as not built rather than mocked up. A screen that says a game counts
            toward your reviews and then counts nothing is the failure this file had. */}
        {/* Named from the shared catalogue rather than written out here. This said
            "Word search is" on all three platforms long after crossword, matching and
            spelling bee had shipped — a learner reaching for minimal pairs was told one
            game existed when four did. */}
        <Banner tone="info">{gamesNotBuiltMessage()}</Banner>
        <Button onClick={() => onNavigate('/learn/words/games/wordsearch')}>
          Play word search
        </Button>
      </main>
    );
  }

  if (queue.isPending) return <Skeleton lines={6} label="Building your puzzle" />;

  const cards = queue.data?.cards ?? [];
  if (cards.length < 3) {
    return (
      <main id="main" className="vl-page">
        <h1>{title}</h1>
        <EmptyState
          title="Not enough words yet"
          body="A puzzle needs at least three words from your deck. Finish a lesson and come back."
          action={{ label: 'Learn new words', onClick: () => onNavigate('/learn/words/deck') }}
        />
      </main>
    );
  }

  return (
    <WordSearchGame
      cards={cards}
      track={track}
      onFinish={(grades) => {
        if (grades.length > 0) {
          void api.call('submitReviewGrades', {
            body: { courseId, batchId: clientUlid(), grades: [...grades] },
          });
        }
        onNavigate('/learn/words');
      }}
    />
  );
}

/**
 * A playable word search, scored into the review schedule.
 *
 * Selection is **two taps, not a drag**: tap the first letter, tap the last. A drag is
 * the obvious gesture and the wrong one here — it is unusable with a keyboard, hostile
 * to a screen reader, and on a phone it fights the page scroll. Two taps is the same
 * `{from, to}` the core model already takes, and it works with every input device.
 */
function WordSearchGame({
  cards,
  track,
  onFinish,
}: {
  readonly cards: readonly { cardId: string; itemId: string; prompt: string }[];
  readonly track: Track;
  readonly onFinish: (grades: readonly GameGrade[]) => void;
}): JSX.Element {
  // One puzzle per learner per day: the same seed gives the same grid on every device,
  // so switching phones mid-game does not reshuffle it.
  const seed = useMemo(() => `wordsearch-${Math.floor(Date.now() / 86_400_000)}`, []);

  const puzzle = useMemo(
    () =>
      generateWordSearch({
        words: cards.map((card) => ({ itemId: card.itemId, text: card.prompt })),
        seed,
        size: 10,
        difficulty: 'normal',
      }),
    [cards, seed],
  );

  const cardIdByItemId = useMemo(
    () => Object.fromEntries(cards.map((card) => [card.itemId, card.cardId])),
    [cards],
  );

  const [state, setState] = useState(() => startSession(Date.now()));
  const [first, setFirst] = useState<Cell | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    track('word_game_start', { type: 'wordsearch', seed });
  }, [seed, track]);

  const labelFor = (itemId: string): string =>
    puzzle.placed.find((placed) => placed.itemId === itemId)?.word ?? itemId;

  const tap = (cell: Cell): void => {
    if (first === null) {
      setFirst(cell);
      setMessage(null);
      return;
    }
    const outcome = applySelection(puzzle, state, { from: first, to: cell });
    setFirst(null);
    setState(outcome.state);
    if (outcome.found === null) {
      setMessage('Not a word — try another pair of ends.');
      return;
    }
    setMessage(
      outcome.duplicate
        ? `Already found ${outcome.found.word}.`
        : `Found ${outcome.found.word}. That counts as a review.`,
    );
  };

  const done = isComplete(puzzle, state);

  const finish = (): void => {
    const now = Date.now();
    const result = resultOf(puzzle, state, cardIdByItemId, now);
    track('word_game_complete', {
      type: 'wordsearch',
      correct: result.correct,
      total: result.total,
      items_scheduled: result.itemsScheduled,
    });
    onFinish(gradesFor(state, cardIdByItemId, now));
  };

  return (
    <main id="main" className="vl-page vl-game">
      <h1>Word search</h1>
      <p className="vl-page__lede">
        Tap the first letter of a word, then its last. {puzzle.placed.length} to find.
      </p>

      <Banner tone="info">
        Everything you find here counts toward your review schedule, the same as a lesson.
      </Banner>

      <table className="vl-game__grid" aria-label="Word search grid">
        <tbody>
          {puzzle.grid.map((row, rowIndex) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: a grid position *is* the identity.
            <tr key={`row-${rowIndex}`}>
              {row.map((letter, colIndex) => {
                const selected = first?.row === rowIndex && first?.col === colIndex;
                return (
                  // biome-ignore lint/suspicious/noArrayIndexKey: a grid position *is* the identity.
                  <td key={`cell-${rowIndex}-${colIndex}`}>
                    <button
                      type="button"
                      className="vl-game__cell"
                      data-selected={selected ? 'true' : 'false'}
                      aria-pressed={selected}
                      aria-label={`Row ${rowIndex + 1} column ${colIndex + 1}, ${letter}`}
                      onClick={() => tap({ row: rowIndex, col: colIndex })}
                    >
                      {letter}
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>

      {/* Live, because the find is the feedback and a sighted learner sees the cell
          change while a screen-reader user would otherwise get nothing. `aria-live`
          without `role="status"`: the info Banner above is already a status region, and
          two of them on one screen makes neither identifiable. */}
      <p className="vl-game__message" aria-live="polite">
        {message ?? '\u00a0'}
      </p>

      <h2>
        Found {state.found.length} of {puzzle.placed.length}
      </h2>
      <ul className="vl-game__found">
        {state.found.map((itemId) => (
          <li key={itemId}>{labelFor(itemId)}</li>
        ))}
      </ul>

      {puzzle.unplaced.length > 0 && (
        // Said rather than swallowed: a word too long for the grid is a content fact,
        // and a learner who knows it is missing will not hunt for it.
        <p className="vl-note">
          {puzzle.unplaced.length} of your words were too long for this grid.
        </p>
      )}

      <Button onClick={finish}>{done ? 'Done — save my reviews' : 'Finish early'}</Button>
    </main>
  );
}

/**
 * A playable crossword, scored into the review schedule as **production**.
 *
 * The harder of the two games we promise, and the reason is the clue: a word search asks
 * whether you can spot a word you are looking at, a crossword asks whether you can
 * produce it from its meaning. So a solved entry grades as `production`, not
 * `recognition` — telling the scheduler the learner did the easier thing would bring the
 * card back too soon.
 */
function CrosswordGame({
  cards,
  track,
  onFinish,
}: {
  readonly cards: readonly { cardId: string; itemId: string; prompt: string; answer: string }[];
  readonly track: Track;
  readonly onFinish: (grades: readonly CrosswordGrade[]) => void;
}): JSX.Element {
  const seed = useMemo(() => `crossword-${Math.floor(Date.now() / 86_400_000)}`, []);

  const puzzle = useMemo(
    () =>
      generateCrossword({
        // The prompt is the target-language word and the answer is its gloss, so the
        // clue is the gloss and the solution is the word — which is the direction that
        // makes this recall rather than reading.
        words: cards.map((card) => ({
          itemId: card.itemId,
          text: card.prompt,
          clue: card.answer,
        })),
        seed,
      }),
    [cards, seed],
  );

  const cardIdByItemId = useMemo(
    () => Object.fromEntries(cards.map((card) => [card.itemId, card.cardId])),
    [cards],
  );

  const [state, setState] = useState(() => startCrossword(Date.now()));

  useEffect(() => {
    track('word_game_start', { type: 'crossword', seed });
  }, [seed, track]);

  const solved = solvedEntries(puzzle, state);
  const solvedIds = new Set(solved.map((entry) => entry.itemId));

  const numberAt = (row: number, col: number): number | null =>
    puzzle.entries.find((entry) => entry.row === row && entry.col === col)?.number ?? null;

  const finish = (): void => {
    const now = Date.now();
    track('word_game_complete', {
      type: 'crossword',
      correct: solved.length,
      total: puzzle.entries.length,
      items_scheduled: crosswordGrades(puzzle, state, cardIdByItemId, now).length,
    });
    onFinish(crosswordGrades(puzzle, state, cardIdByItemId, now));
  };

  if (puzzle.entries.length === 0) {
    return (
      <main id="main" className="vl-page">
        <h1>Crossword</h1>
        {/* Said rather than shown as an empty board: these words share no letters, so
            there is no crossword to build from them today. */}
        <Banner tone="info">
          None of today&apos;s words interlock, so there is no crossword to build. Word search works
          from the same deck.
        </Banner>
        <Button onClick={() => onFinish([])}>Back to Words</Button>
      </main>
    );
  }

  return (
    <main id="main" className="vl-page vl-game">
      <h1>Crossword</h1>
      <p className="vl-page__lede">
        {puzzle.entries.length} clues from your own words. Solved {solved.length}.
      </p>

      <Banner tone="info">
        Everything you solve here counts toward your review schedule, as speaking practice counts —
        you produced the word, you did not just recognise it.
      </Banner>

      <table className="vl-game__grid vl-game__grid--crossword" aria-label="Crossword grid">
        <tbody>
          {puzzle.grid.map((row, rowIndex) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: a grid position *is* the identity.
            <tr key={`cw-row-${rowIndex}`}>
              {row.map((cell, colIndex) => {
                if (cell === null) {
                  return (
                    // biome-ignore lint/suspicious/noArrayIndexKey: a grid position *is* the identity.
                    <td key={`cw-${rowIndex}-${colIndex}`} className="vl-game__block" aria-hidden />
                  );
                }
                const number = numberAt(rowIndex, colIndex);
                return (
                  // biome-ignore lint/suspicious/noArrayIndexKey: a grid position *is* the identity.
                  <td key={`cw-${rowIndex}-${colIndex}`} className="vl-game__square">
                    {number !== null && <span className="vl-game__number">{number}</span>}
                    <input
                      className="vl-game__input"
                      type="text"
                      inputMode="text"
                      maxLength={1}
                      value={state.letters[`${rowIndex},${colIndex}`] ?? ''}
                      aria-label={`Row ${rowIndex + 1} column ${colIndex + 1}`}
                      onChange={(event) =>
                        setState(typeLetter(state, rowIndex, colIndex, event.target.value))
                      }
                    />
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>

      <h2>Clues</h2>
      <ol className="vl-game__clues">
        {puzzle.entries.map((entry) => (
          <li
            key={`${entry.number}-${entry.direction}`}
            data-solved={solvedIds.has(entry.itemId) ? 'true' : 'false'}
          >
            {entry.number} {entry.direction === 'across' ? 'Across' : 'Down'}: {entry.clue} (
            {entry.length})
          </li>
        ))}
      </ol>

      {puzzle.unplaced.length > 0 && (
        <p className="vl-note">
          {puzzle.unplaced.length} of your words did not interlock with the rest today.
        </p>
      )}

      <Button onClick={finish}>
        {isCrosswordComplete(puzzle, state) ? 'Done — save my reviews' : 'Finish early'}
      </Button>
    </main>
  );
}

/**
 * Matching: pair each word with its meaning.
 *
 * Grades as `recognition` — the meaning is on screen, so this is choosing, not recalling.
 */
function MatchingGame({
  cards,
  track,
  onFinish,
}: {
  readonly cards: readonly { cardId: string; itemId: string; prompt: string; answer: string }[];
  readonly track: Track;
  readonly onFinish: (grades: readonly GameModalityGrade[]) => void;
}): JSX.Element {
  const seed = useMemo(() => `matching-${Math.floor(Date.now() / 86_400_000)}`, []);
  const board = useMemo(
    () =>
      buildMatching(
        cards.map((card) => ({ itemId: card.itemId, term: card.prompt, gloss: card.answer })),
        seed,
      ),
    [cards, seed],
  );
  const cardIdByItemId = useMemo(
    () => Object.fromEntries(cards.map((card) => [card.itemId, card.cardId])),
    [cards],
  );

  const [state, setState] = useState(() => startMatching(Date.now()));
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    track('word_game_start', { type: 'matching', seed });
  }, [seed, track]);

  const done = isMatchingComplete(board, state);

  return (
    <main id="main" className="vl-page vl-game">
      <h1>Matching</h1>
      <p className="vl-page__lede">
        Tap a word, then its meaning. Matched {state.matched.length} of {board.terms.length}.
      </p>
      <Banner tone="info">Every pair you make counts toward your review schedule.</Banner>

      <div className="vl-game__columns">
        <ul className="vl-game__column" aria-label="Words">
          {board.terms.map((pair) => (
            <li key={pair.itemId}>
              <Button
                variant={state.pickedTerm === pair.itemId ? 'primary' : 'secondary'}
                size="sm"
                disabled={state.matched.includes(pair.itemId)}
                onClick={() => {
                  setMessage(null);
                  setState(tapTerm(state, pair.itemId));
                }}
              >
                {pair.term}
              </Button>
            </li>
          ))}
        </ul>
        <ul className="vl-game__column" aria-label="Meanings">
          {board.glosses.map((pair) => (
            <li key={pair.itemId}>
              <Button
                variant="secondary"
                size="sm"
                disabled={state.matched.includes(pair.itemId)}
                onClick={() => {
                  const outcome = tapGloss(state, pair.itemId);
                  setState(outcome.state);
                  setMessage(outcome.paired ? 'Paired.' : 'Not that one — try again.');
                }}
              >
                {pair.gloss}
              </Button>
            </li>
          ))}
        </ul>
      </div>

      <p className="vl-game__message" aria-live="polite">
        {message ?? '\u00a0'}
      </p>

      <Button
        onClick={() => {
          const now = Date.now();
          const grades = matchingGrades(state, cardIdByItemId, now);
          track('word_game_complete', {
            type: 'matching',
            correct: state.matched.length,
            total: board.terms.length,
            items_scheduled: grades.length,
          });
          onFinish(grades);
        }}
      >
        {done ? 'Done — save my reviews' : 'Finish early'}
      </Button>
    </main>
  );
}

/**
 * Spelling bee: spell the word from its meaning.
 *
 * Grades as `production`, and it is the one game that submits a wrong answer as an
 * incorrect review — the learner was asked to spell a word and could not, which is a
 * fact about recall. An unfound word in a search is not.
 */
function SpellingGame({
  cards,
  track,
  onFinish,
}: {
  readonly cards: readonly { cardId: string; itemId: string; prompt: string; answer: string }[];
  readonly track: Track;
  readonly onFinish: (grades: readonly GameModalityGrade[]) => void;
}): JSX.Element {
  const seed = useMemo(() => `spelling-${Math.floor(Date.now() / 86_400_000)}`, []);
  const prompts = useMemo(
    () =>
      cards
        .slice(0, 10)
        .map((card) => ({ itemId: card.itemId, answer: card.prompt, clue: card.answer })),
    [cards],
  );
  const cardIdByItemId = useMemo(
    () => Object.fromEntries(cards.map((card) => [card.itemId, card.cardId])),
    [cards],
  );

  const [state, setState] = useState(() => startSpelling(Date.now()));
  const [attempt, setAttempt] = useState('');
  const [feedback, setFeedback] = useState<string | null>(null);

  useEffect(() => {
    track('word_game_start', { type: 'spelling', seed });
  }, [seed, track]);

  const done = isSpellingComplete(prompts, state);
  const prompt = prompts[state.index];

  const finish = (): void => {
    const now = Date.now();
    const grades = spellingGrades(state, cardIdByItemId, now);
    track('word_game_complete', {
      type: 'spelling',
      correct: state.correct.length,
      total: prompts.length,
      items_scheduled: grades.length,
    });
    onFinish(grades);
  };

  return (
    <main id="main" className="vl-page vl-game">
      <h1>Spelling bee</h1>
      <p className="vl-page__lede">
        {state.index} of {prompts.length} · {state.correct.length} right
      </p>
      <Banner tone="info">
        Accents are forgiven — a keyboard without ñ is not a spelling mistake.
      </Banner>

      {prompt !== undefined ? (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            const outcome = submitSpelling(prompts, state, attempt);
            setState(outcome.state);
            // Revealed either way: being told you are wrong and not what the word was
            // is the one outcome with no teaching in it.
            setFeedback(
              outcome.correct ? `Right — ${outcome.answer}.` : `It was ${outcome.answer}.`,
            );
            setAttempt('');
          }}
        >
          <label htmlFor="vl-spell">
            How do you spell <strong>{prompt.clue}</strong>?
          </label>
          <input
            id="vl-spell"
            className="vl-input"
            value={attempt}
            autoComplete="off"
            autoCapitalize="off"
            spellCheck={false}
            onChange={(event) => setAttempt(event.target.value)}
          />
          <Button type="submit">Check</Button>
        </form>
      ) : (
        <p>
          All done. {state.correct.length} of {prompts.length} right.
        </p>
      )}

      <p className="vl-game__message" aria-live="polite">
        {feedback ?? '\u00a0'}
      </p>

      <Button onClick={finish}>{done ? 'Done — save my reviews' : 'Finish early'}</Button>
    </main>
  );
}

// ── C13–C14 Grammar ─────────────────────────────────────────────────────────

export function GrammarListScreen({ onNavigate }: NavigationProps): JSX.Element {
  const api = useApi();
  const courseId = useCourseId();
  const { track } = useApp();
  const query = useQuery({
    queryKey: ['grammar', courseId] as const,
    queryFn: () => api.call('listGrammarConcepts', { query: { courseId } }),
  });

  // The mastery map, per concept. The product claim is that grammar is tied to the
  // learner's own errors rather than ordered as a syllabus, and the shape of this map
  // over time is what would show that.
  const concepts = query.data?.concepts;
  useEffect(() => {
    if (concepts === undefined) return;
    track('grammar_home_view', {
      mastery_map: Object.fromEntries(concepts.map((concept) => [concept.id, concept.mastery])),
    });
  }, [concepts, track]);

  return (
    <main id="main" className="vl-page">
      <h1>Grammar</h1>
      <p className="vl-page__lede">
        Short explanations, tied to mistakes you actually made. Nothing here is a wall of rules to
        read first.
      </p>

      {query.isPending && <Skeleton lines={5} label="Loading grammar" />}
      {query.error !== null && (
        <ErrorState
          title="We couldn't load grammar"
          {...retryIf(query.error, () => void query.refetch())}
        />
      )}

      {query.data !== undefined && (
        <ul className="vl-grammar-list">
          {(query.data as GrammarList).concepts.map((concept) => (
            <li key={concept.id}>
              <button
                type="button"
                className="vl-grammar-row"
                onClick={() => onNavigate(`/learn/grammar/${concept.id}`)}
              >
                <span className="vl-grammar-row__name">{concept.name}</span>
                <span className="vl-grammar-row__meta">{concept.cefr}</span>
                {/* Mastery as a bar *and* a number, never colour alone. The bar is
                    decorative; the percentage carries the meaning. */}
                <span className="vl-grammar-row__mastery" aria-hidden="true">
                  <span style={{ width: `${Math.round(concept.mastery * 100)}%` }} />
                </span>
                <span className="vl-grammar-row__percent">
                  {Math.round(concept.mastery * 100)}% mastered
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </main>
  );
}

export function GrammarConceptScreen({
  conceptId,
  onNavigate,
}: NavigationProps & { readonly conceptId: string }): JSX.Element {
  const api = useApi();
  const { track } = useApp();
  const query = useQuery({
    queryKey: ['grammar-concept', conceptId] as const,
    queryFn: () => api.call('getGrammarConcept', { params: { conceptId } }),
  });

  useEffect(() => {
    track('concept_view', { id: conceptId });
  }, [conceptId, track]);

  if (query.isPending) return <Skeleton lines={5} label="Loading this concept" />;
  if (query.error !== null || query.data === undefined) {
    return (
      <ErrorState
        title="We couldn't load this concept"
        {...retryIf(query.error, () => void query.refetch())}
        alternative={{ label: 'Back to grammar', onClick: () => onNavigate('/learn/grammar') }}
      />
    );
  }

  const concept = query.data;

  return (
    <main id="main" className="vl-page">
      <h1>{concept.name}</h1>
      <p className="vl-page__lede">{concept.explainer}</p>

      {/* Contrast against the learner's own first language. Explaining why *they*
          specifically get this wrong is more useful than a universal rule. */}
      {concept.l1Contrast !== null && (
        <section className="vl-l1-contrast">
          <h2>Why this trips you up</h2>
          <p>{concept.l1Contrast}</p>
        </section>
      )}

      <section>
        <h2>Examples</h2>
        <ul className="vl-examples">
          {concept.examples.map((example) => (
            <li key={example.text}>
              <span>{example.text}</span>
              <span className="vl-examples__translation">{example.translation}</span>
              {example.note !== null && <span className="vl-examples__note">{example.note}</span>}
            </li>
          ))}
        </ul>
      </section>

      {concept.commonError !== null && (
        <section>
          <h2>The usual slip</h2>
          <p>{concept.commonError}</p>
        </section>
      )}

      <Button
        onClick={() => {
          // Reaching the end of a concept and acting on it is the completion that
          // matters here; scrolling to the bottom is not.
          track('concept_complete', { accuracy: null });
          // The error that sent them here is the useful half: a clinic entered from a
          // concept nobody gets wrong is a syllabus, which is what we said we would not
          // build.
          track('grammar_clinic_tapped', { top_error: concept.commonError ?? null });
          onNavigate('/review/mistakes');
        }}
      >
        Practise this in your mistakes
      </Button>
    </main>
  );
}

// ── C15–C16 Listening ───────────────────────────────────────────────────────

export function ListenListScreen({ onNavigate }: NavigationProps): JSX.Element {
  const api = useApi();
  const courseId = useCourseId();
  const { track } = useApp();
  const [freshOnly, setFreshOnly] = useState(false);

  const query = useQuery({
    queryKey: qk.listening(courseId),
    queryFn: () => api.call('listListening', { query: { courseId, limit: 40 } }),
  });

  // How much there is to listen to at the learner's level. The weekly-drop promise is
  // a content commitment, and an empty list here is the first sign of breaking it.
  const listening = query.data as ListeningList | undefined;
  useEffect(() => {
    if (listening === undefined) return;
    track('listen_home_view', {
      level: listening.items[0]?.cefr ?? null,
      items: listening.items.length,
    });
  }, [listening, track]);

  return (
    <main id="main" className="vl-page">
      <h1>Listen</h1>
      <p className="vl-page__lede">
        Real speech at real speed — people interrupting each other, not a textbook recording. New
        drops land every week.
      </p>

      <label className="vl-toggle">
        <input
          type="checkbox"
          checked={freshOnly}
          onChange={(event) => setFreshOnly(event.target.checked)}
        />
        This week only
      </label>

      {query.isPending && <Skeleton lines={5} label="Loading listening" />}
      {query.error !== null && (
        <ErrorState
          title="We couldn't load listening"
          {...retryIf(query.error, () => void query.refetch())}
        />
      )}

      {query.data !== undefined &&
        (() => {
          const items = (query.data as ListeningList).items.filter(
            (item) => !freshOnly || item.isFresh,
          );
          if (items.length === 0) {
            return (
              <EmptyState
                title={freshOnly ? 'Nothing new this week yet' : 'Nothing here yet'}
                body="Drops land on Mondays."
                action={{
                  label: freshOnly ? 'Show everything' : 'Back to Learn',
                  onClick: () => (freshOnly ? setFreshOnly(false) : onNavigate('/learn')),
                }}
              />
            );
          }
          return (
            <ul className="vl-listen-list">
              {items.map((item) => (
                <li key={item.id}>
                  <button
                    type="button"
                    className="vl-listen-row"
                    onClick={() => onNavigate(`/learn/listen/${item.id}`)}
                  >
                    <span className="vl-listen-row__title">{item.title}</span>
                    <span className="vl-listen-row__context">{item.context}</span>
                    <span className="vl-listen-row__meta">
                      {Math.round(item.durationSeconds / 60) || 1} min · {item.cefr} ·{' '}
                      {item.speakerAccent}
                      {item.isFresh && ' · new'}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          );
        })()}
    </main>
  );
}

export function ListeningItemScreen({
  itemId,
  onNavigate,
}: NavigationProps & { readonly itemId: string }): JSX.Element {
  const api = useApi();
  const { track } = useApp();
  const [showTranscript, setShowTranscript] = useState(false);
  const [showTranslation, setShowTranslation] = useState(false);

  const query = useQuery({
    queryKey: ['listening', itemId] as const,
    queryFn: () => api.call('getListeningItem', { params: { itemId } }),
  });

  if (query.isPending) return <Skeleton lines={6} label="Loading this clip" />;
  if (query.error !== null || query.data === undefined) {
    return (
      <ErrorState
        title="We couldn't load this clip"
        {...retryIf(query.error, () => void query.refetch())}
        alternative={{ label: 'Back to Listen', onClick: () => onNavigate('/learn/listen') }}
      />
    );
  }

  const item: ListeningItem = query.data;

  return (
    <main id="main" className="vl-page">
      <h1>{item.title}</h1>
      <p className="vl-page__lede">{item.context}</p>

      {/* eslint-disable-next-line jsx-a11y/media-has-caption -- the transcript below
          is the caption, and it is deliberately hidden on first listen. */}
      <audio
        className="vl-audio"
        controls
        // Resolved against the API, not the page: the URL arrives root-relative and an
        // `<audio src>` would otherwise ask Pages for a file only the Worker has.
        src={mediaUrl(item.audioUrl) ?? undefined}
        preload="none"
        // The playback rate is the measurement that matters: "real speech at real
        // speed" is the promise, and a learner who always slows it down is telling us
        // the level is wrong.
        onPlay={(event) =>
          track('listen_item_play', { id: item.id, speed: event.currentTarget.playbackRate })
        }
      >
        <track kind="captions" />
      </audio>

      {/* Transcript hidden by default. Reading along on the first pass removes the
          listening, which is the thing being practised. */}
      <div className="vl-listen__controls">
        <Button variant="secondary" onClick={() => setShowTranscript(!showTranscript)}>
          {showTranscript ? 'Hide transcript' : 'Show transcript'}
        </Button>
        {showTranscript && (
          <Button variant="ghost" onClick={() => setShowTranslation(!showTranslation)}>
            {showTranslation ? 'Hide translation' : 'Show translation'}
          </Button>
        )}
      </div>

      <div className="vl-listen__practice">
        {/* Shadowing is the exercise this screen exists for: repeating along with real
            speech at real speed is the thing a textbook recording cannot teach. */}
        <Button
          variant="secondary"
          onClick={() => {
            // The score arrives from the scorer, not from here, so it goes as a null.
            track('listen_shadow', { score: null });
            onNavigate('/talk/open/session');
          }}
        >
          Shadow this
        </Button>
        <Button
          variant="ghost"
          onClick={() => {
            // Marked by the learner, which is honest about what it is: a self-report
            // of comprehension rather than a graded quiz.
            track('listen_quiz_complete', { accuracy: null });
            setShowTranscript(true);
          }}
        >
          I understood it — show me the transcript
        </Button>
      </div>

      {showTranscript && (
        <ol className="vl-transcript">
          {item.transcript.map((line) => (
            <li key={line.startMs}>
              <p className="vl-transcript__text">
                {line.tokens.map((token, index) =>
                  token.itemId === null ? (
                    // biome-ignore lint/suspicious/noArrayIndexKey: tokens repeat within a line, so position is part of their identity; the list never reorders.
                    <span key={`${token.text}-${index}`}>{token.text} </span>
                  ) : (
                    <button
                      // biome-ignore lint/suspicious/noArrayIndexKey: tokens repeat within a line, so position is part of their identity; the list never reorders.
                      key={`${token.text}-${index}`}
                      type="button"
                      className="vl-transcript__token"
                      onClick={() => {
                        // A word taken from real speech is the strongest kind of
                        // addition, so where it came from is recorded with it.
                        track('listen_word_added', { item: token.itemId ?? '' });
                        onNavigate(`/learn/words/${token.itemId}`);
                      }}
                    >
                      {token.text}
                    </button>
                  ),
                )}
              </p>
              {showTranslation && <p className="vl-transcript__translation">{line.translation}</p>}
            </li>
          ))}
        </ol>
      )}
    </main>
  );
}

// ── H1 Library (web only) ───────────────────────────────────────────────────

/**
 * A module constant rather than a local: rebuilt each render it would make the view
 * event fire on every render instead of once per visit.
 */
const LIBRARY_SECTIONS = [
  ['Lessons', 'Everything in your course, searchable', '/learn/path'],
  ['Conversations', 'The whole scenario library', '/talk/scenarios'],
  ['Listening', 'Every drop, including ones you have heard', '/learn/listen'],
  ['Grammar', 'All concepts, not just the ones you have hit', '/learn/grammar'],
  ['Words', 'Your deck plus everything the course teaches', '/learn/words/deck'],
] as const;

export function LibraryScreen({ onNavigate }: NavigationProps): JSX.Element {
  const { track } = useApp();
  const [facet, setFacet] = useState<string>('all');

  // Web-only, and that is the point of measuring it: whether a big screen is used to
  // browse the catalogue is the question that decides if this surface earns its upkeep.
  useEffect(() => {
    track('library_view', { items: LIBRARY_SECTIONS.length, filters: [] });
  }, [track]);

  return (
    <main id="main" className="vl-page">
      <h1>Library</h1>
      {/* A web-only surface: a big screen can show the whole catalogue at once, which
          is genuinely better than a phone's one-thing-at-a-time path. */}
      <p className="vl-page__lede">
        Everything in your course in one place. The app shows you one next thing; this is for when
        you want to go looking.
      </p>
      {/* A facet filter, because the point of this surface is browsing a catalogue and
          a catalogue you cannot narrow is a list. */}
      <nav className="vl-chip-filters" aria-label="Filter the library">
        {['all', 'lessons', 'conversations', 'listening', 'grammar', 'words'].map((option) => (
          <button
            key={option}
            type="button"
            className="vl-chip-filter"
            aria-pressed={facet === option}
            onClick={() => {
              track('library_filter', { facet: 'kind', value: option });
              setFacet(option);
            }}
          >
            {option === 'all' ? 'Everything' : option}
          </button>
        ))}
      </nav>

      <ul className="vl-tile-grid">
        {LIBRARY_SECTIONS.filter(([title]) => facet === 'all' || title.toLowerCase() === facet).map(
          ([title, detail, route]) => (
            <li key={route}>
              <button
                type="button"
                className="vl-tile"
                onClick={() => {
                  track('library_item_open', { kind: title.toLowerCase(), id: route });
                  onNavigate(route);
                }}
              >
                <span className="vl-tile__title">{title}</span>
                <span className="vl-tile__detail">{detail}</span>
              </button>
            </li>
          ),
        )}
      </ul>
    </main>
  );
}

// ── H2 Workbook (web only) ──────────────────────────────────────────────────

export function WorkbookScreen({ onNavigate }: NavigationProps): JSX.Element {
  const api = useApi();
  const { track } = useApp();
  const courseId = useCourseId();
  const query = useQuery({
    queryKey: qk.mistakes(courseId, 'open'),
    queryFn: () => api.call('listMistakes', { query: { courseId, status: 'open', limit: 100 } }),
  });

  // This screen emitted nothing at all, so there was no way to tell whether written
  // practice was used — the count is on the view because an empty workbook and a full
  // one are different screens for the learner and should be different numbers for us.
  const openCount = query.data?.items.length;
  useEffect(() => {
    if (openCount === undefined) return;
    track('workbook_view', { open_mistakes: openCount });
  }, [openCount, track]);

  return (
    <main id="main" className="vl-page">
      <h1>Workbook</h1>
      <p className="vl-page__lede">
        Written practice on everything you have got wrong. Typing is slower than speaking, which is
        exactly why it helps on the things that keep slipping — and it uses no speaking time.
      </p>

      {query.isPending && <Skeleton lines={6} label="Loading your workbook" />}
      {query.error !== null && (
        <ErrorState
          title="We couldn't load your workbook"
          {...retryIf(query.error, () => void query.refetch())}
        />
      )}

      {query.data !== undefined &&
        (query.data.items.length === 0 ? (
          <EmptyState
            title="Nothing to work through"
            body="Your open mistakes land here as you make them."
            action={{ label: 'Back to today', onClick: () => onNavigate('/') }}
          />
        ) : (
          <ol className="vl-workbook">
            {query.data.items.map((mistake) => (
              <li key={mistake.id} className="vl-workbook__item">
                <p className="vl-workbook__said">You said: {mistake.original}</p>
                {mistake.corrected !== null && (
                  <p className="vl-workbook__target">Better: {mistake.corrected}</p>
                )}
                <p className="vl-workbook__why">{mistake.explanation}</p>
                {/* Severity is stated, so a nitpick does not read like a blocker. */}
                <p className="vl-workbook__severity" data-severity={mistake.severity}>
                  {mistake.severity === 'blocking'
                    ? 'Gets in the way of being understood'
                    : mistake.severity === 'notable'
                      ? 'Worth fixing'
                      : 'A nitpick — fix it when you feel like it'}
                </p>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    // Severity, not id: the question this answers is whether learners
                    // work on what blocks them or on the nitpicks.
                    track('workbook_practise_tapped', { severity: mistake.severity });
                    onNavigate(`/review/mistakes?focus=${mistake.id}`);
                  }}
                >
                  Practise this one
                </Button>
              </li>
            ))}
          </ol>
        ))}
    </main>
  );
}

// ── E5 Saved items ──────────────────────────────────────────────────────────

type SavedKind = 'word' | 'phrase' | 'turn' | 'clip';

/**
 * The learner's own collection, with an export button.
 *
 * The export is the reason this screen matters more than it looks. Everything else
 * here is a convenience; a working CSV and Anki export is the claim that the
 * collection belongs to the learner and not to us, and a claim like that is only worth
 * anything if it is a button rather than a support request.
 */
export function SavedItemsScreen({ onNavigate }: NavigationProps): JSX.Element {
  const api = useApi();
  const courseId = useCourseId();
  const { track } = useApp();
  const [kind, setKind] = useState<SavedKind | null>(null);

  const query = useQuery({
    queryKey: ['saved', courseId, kind ?? 'all'] as const,
    queryFn: () =>
      api.call('listSaved', {
        query: { courseId, limit: 100, ...(kind === null ? {} : { kind }) },
      }),
  });

  useEffect(() => {
    track('saved_items_view', {});
  }, [track]);

  const exportSaved = useMutation({
    mutationFn: (format: 'csv' | 'anki') => api.call('exportSaved', { body: { courseId, format } }),
    onSuccess: (result) => {
      // Downloaded from the response rather than from a signed URL: the collection is
      // small, and a URL for it is one more thing that can expire between the tap and
      // the download.
      const blob = new Blob([result.content], {
        type: result.format === 'csv' ? 'text/csv' : 'text/tab-separated-values',
      });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = result.filename;
      anchor.click();
      URL.revokeObjectURL(url);
    },
  });

  const filters: readonly [SavedKind | null, string][] = [
    [null, 'Everything'],
    ['word', 'Words'],
    ['phrase', 'Phrases'],
    ['turn', 'Turns'],
    ['clip', 'Your clips'],
  ];

  return (
    <main id="main" className="vl-page vl-saved">
      <h1>Saved</h1>

      <nav className="vl-chip-filters" aria-label="Filter saved items">
        {filters.map(([id, label]) => (
          <button
            key={label}
            type="button"
            className="vl-chip-filter"
            aria-pressed={kind === id}
            onClick={() => setKind(id)}
          >
            {label}
          </button>
        ))}
      </nav>

      {query.isPending && <Skeleton lines={5} label="Loading your saved items" />}
      {query.error !== null && (
        <ErrorState
          title="We couldn't load your saved items"
          {...retryIf(query.error, () => void query.refetch())}
        />
      )}

      {query.data !== undefined && query.data.items.length === 0 && (
        <EmptyState
          title="Nothing saved yet"
          // Explains what saving does, rather than just reporting that nothing is here.
          body="Tap save on a word, a phrase or one of your own recordings and it lands here. Saving does not add anything to your review queue — it is a bookmark, not an obligation."
          action={{ label: 'Browse your deck', onClick: () => onNavigate('/learn/words/deck') }}
        />
      )}

      {query.data !== undefined && query.data.items.length > 0 && (
        <>
          <ul className="vl-saved__list">
            {query.data.items.map((item) => (
              <li key={item.id} className="vl-saved__row" data-kind={item.kind}>
                <span className="vl-saved__display">{item.display}</span>
                {item.translation !== null && (
                  <span className="vl-saved__translation">{item.translation}</span>
                )}
                {item.audioUrl !== null && (
                  // eslint-disable-next-line jsx-a11y/media-has-caption -- the learner's own recording; the saved text is the caption.
                  <audio
                    className="vl-saved__audio"
                    controls
                    src={mediaUrl(item.audioUrl) ?? undefined}
                    preload="none"
                  >
                    <track kind="captions" />
                  </audio>
                )}
                {item.sourceRoute !== null && (
                  <button
                    type="button"
                    className="vl-link"
                    onClick={() => onNavigate(item.sourceRoute ?? '/learn')}
                  >
                    Where this came from
                  </button>
                )}
              </li>
            ))}
          </ul>

          <div className="vl-step__actions">
            {(['csv', 'anki'] as const).map((format) => (
              <Button
                key={format}
                variant="secondary"
                loading={exportSaved.isPending}
                onClick={() => {
                  track('saved_export', { format });
                  exportSaved.mutate(format);
                }}
              >
                Export {format === 'csv' ? 'as CSV' : 'for Anki'}
              </Button>
            ))}
          </div>
          <p className="vl-note">
            The export is the whole file, not a sample, and it is not rate-limited. This is your
            collection.
          </p>
        </>
      )}
    </main>
  );
}

/**
 * The card stack. Swipe right to know, left to not, up to save, down to skip.
 *
 * This exact interaction is the thing the competitor's reviewers love most — one of
 * them says it "takes away all anxiety stemming from the desire to swipe" — and it is
 * worth copying precisely because the anxiety is the thing that stops people
 * practising.
 *
 * Gestures are not the only way in. Arrow keys do the same four things and so do four
 * visible buttons: a swipe-only deck is unusable with a keyboard, unusable with a
 * switch device, and unusable one-handed on a large phone. The buttons are not a
 * fallback, they are the same control.
 *
 * Undo covers the last card, because the most common swipe error is a confident one.
 */
function SwipeDeck({
  cards,
  onComplete,
  track,
}: {
  readonly cards: readonly {
    readonly itemId: string;
    readonly prompt: string;
    readonly answer: string;
  }[];
  readonly onComplete: (known: number, total: number) => void;
  readonly track: Track;
}): JSX.Element {
  const [index, setIndex] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [known, setKnown] = useState(0);
  const [history, setHistory] = useState<readonly { index: number; known: boolean }[]>([]);
  const shownAt = useRef(Date.now());
  const reported = useRef(false);

  const card = cards[index];

  const commit = (direction: 'right' | 'left' | 'up' | 'down') => {
    if (card === undefined) return;
    track('swipe_card', {
      item: card.itemId,
      direction,
      // Whether they flipped before deciding is the difference between recognition and
      // recall, which are different measurements of the same card.
      flipped,
      ms: Date.now() - shownAt.current,
    });

    const counted = direction === 'right';
    if (counted) setKnown((current) => current + 1);
    setHistory((current) => [...current, { index, known: counted }]);
    setFlipped(false);
    shownAt.current = Date.now();
    setIndex((current) => current + 1);
  };

  const undo = () => {
    const last = history.at(-1);
    if (last === undefined) return;
    if (last.known) setKnown((current) => current - 1);
    setHistory((current) => current.slice(0, -1));
    setIndex(last.index);
    setFlipped(false);
    shownAt.current = Date.now();
  };

  if (card === undefined) {
    if (!reported.current && cards.length > 0) {
      reported.current = true;
      track('swipe_deck_complete', {
        count: cards.length,
        known_pct: Math.round((known / cards.length) * 100),
      });
      onComplete(known, cards.length);
    }
    return (
      <EmptyState
        title="Deck done"
        // Names the number that needs work rather than only the score: the second
        // number is the one that tells the learner what to do next.
        body={`${cards.length} words, ${cards.length - known} need work. They are in your review queue.`}
        action={{ label: 'Review them now', onClick: () => undefined }}
      />
    );
  }

  return (
    <section
      className="vl-swipe"
      aria-label="Word deck"
      // The keyboard does the same four things as the gestures.
      onKeyDown={(event) => {
        const map: Record<string, 'right' | 'left' | 'up' | 'down'> = {
          ArrowRight: 'right',
          ArrowLeft: 'left',
          ArrowUp: 'up',
          ArrowDown: 'down',
        };
        const direction = map[event.key];
        if (direction !== undefined) {
          event.preventDefault();
          commit(direction);
        } else if (event.key === ' ' || event.key === 'Enter') {
          event.preventDefault();
          setFlipped((current) => !current);
        }
      }}
      // biome-ignore lint/a11y/noNoninteractiveTabindex: the deck itself is the control surface for the arrow keys; the buttons below duplicate every action for anyone who would rather not use it.
      tabIndex={0}
    >
      <p className="vl-swipe__progress" aria-live="polite">
        {index + 1} of {cards.length}
      </p>

      <button
        type="button"
        className="vl-swipe__card"
        data-flipped={flipped}
        onClick={() => setFlipped((current) => !current)}
        aria-label={flipped ? 'Show the word' : 'Show the meaning'}
      >
        <span className="vl-swipe__front">{card.prompt}</span>
        {flipped && <span className="vl-swipe__back">{card.answer}</span>}
      </button>

      <div className="vl-swipe__actions">
        {(
          [
            ['left', "Don't know"],
            ['right', 'Know it'],
            ['up', 'Save'],
            ['down', 'Skip'],
          ] as const
        ).map(([direction, label]) => (
          <button
            key={direction}
            type="button"
            className="vl-button vl-button--secondary"
            onClick={() => commit(direction)}
          >
            {label}
          </button>
        ))}
      </div>

      {/* The most common swipe error is a confident one. */}
      {history.length > 0 && (
        <button type="button" className="vl-button vl-button--ghost" onClick={undo}>
          Undo
        </button>
      )}
    </section>
  );
}

// ── C13 Conjugation trainer ─────────────────────────────────────────────────

/**
 * A tense × person grid you fill in, with mastery kept per cell.
 *
 * Per cell is what makes this different from every conjugation drill that feels like
 * being tested on things you already know. "I can do the preterite except for
 * *vosotros*" is the real shape of the knowledge, and `Drill my weak cells` can only
 * exist if that shape is recorded.
 *
 * Irregulars are highlighted *after* completion rather than before. Marking them up
 * front tells the learner which ones to think about, which is the part they are
 * supposed to be learning.
 */
export function ConjugationScreen({ onNavigate }: NavigationProps): JSX.Element {
  const api = useApi();
  const courseId = useCourseId();
  const { track } = useApp();

  const [lemma, setLemma] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [entries, setEntries] = useState<Record<string, string>>({});

  const query = useQuery({
    queryKey: ['conjugation', courseId, lemma] as const,
    queryFn: () => api.call('getConjugation', { query: { courseId, lemma } }),
    enabled: lemma.length > 0,
  });

  const submit = useMutation({
    mutationFn: (cells: { tense: string; person: string; correct: boolean }[]) =>
      api.call('submitConjugation', {
        body: { courseId, lemma, mode: 'typed', cells },
      }),
    onSuccess: (result) => {
      setSubmitted(true);
      track('conjugation_session', {
        verb: lemma,
        // The tenses actually attempted, rather than a single tense: the grid spans
        // several and reporting one would misattribute the accuracy.
        tense: [...new Set((query.data?.cells ?? []).map((cell) => cell.tense))].join('+'),
        accuracy: result.accuracy,
        mode: 'typed',
      });
    },
  });

  const grid = query.data;
  const tenses = [...new Set((grid?.cells ?? []).map((cell) => cell.tense))];

  return (
    <main id="main" className="vl-page vl-conjugation">
      <h1>Conjugation</h1>

      <form
        className="vl-conjugation__pick"
        onSubmit={(event) => {
          event.preventDefault();
          setSubmitted(false);
          setEntries({});
        }}
      >
        <label className="vl-field">
          <span className="vl-field__label">Which verb?</span>
          <input
            className="vl-field__input"
            value={lemma}
            onChange={(event) => setLemma(event.target.value.trim())}
            placeholder="tener"
            autoComplete="off"
          />
        </label>
      </form>

      {query.isPending && lemma.length > 0 && <Skeleton lines={5} label="Loading the grid" />}

      {/* A typo and a content gap are different problems, and the server distinguishes
          them, so the screen does too. */}
      {grid !== undefined && !grid.found && (
        <EmptyState
          title={`We don't have "${lemma}"`}
          body="Check the spelling, or pick it from your deck — the trainer works from the same items your lessons use."
          action={{ label: 'Open your deck', onClick: () => onNavigate('/learn/words/deck') }}
        />
      )}

      {grid?.found === true && grid.cells.length === 0 && (
        <EmptyState
          title="No conjugations for this verb yet"
          body="That is a gap on our side rather than yours. The word itself still works everywhere else."
          action={{ label: 'Open your deck', onClick: () => onNavigate('/learn/words/deck') }}
        />
      )}

      {grid !== undefined && grid.cells.length > 0 && (
        <>
          {tenses.map((tense) => (
            <section key={tense} aria-label={tense}>
              <h2>{tense}</h2>
              <ul className="vl-conjugation__grid">
                {grid.cells
                  .filter((cell) => cell.tense === tense)
                  .map((cell) => {
                    const key = `${cell.tense}:${cell.person}`;
                    const typed = entries[key] ?? '';
                    const right = typed.trim().toLowerCase() === cell.form.toLowerCase();
                    return (
                      <li
                        key={key}
                        className="vl-conjugation__cell"
                        // Irregulars revealed only after completion: marking them up
                        // front tells the learner the answer to the question.
                        data-irregular={submitted && cell.irregular}
                        data-correct={submitted ? right : undefined}
                      >
                        <label className="vl-field">
                          <span className="vl-field__label">{cell.person}</span>
                          <input
                            className="vl-field__input"
                            value={typed}
                            disabled={submitted}
                            onChange={(event) =>
                              setEntries((current) => ({ ...current, [key]: event.target.value }))
                            }
                          />
                        </label>
                        {submitted && !right && (
                          <span className="vl-conjugation__answer">{cell.form}</span>
                        )}
                        {/* Their own history with this cell, which is the thing a
                            per-verb score cannot show. */}
                        {cell.attempts > 0 && (
                          <span className="vl-conjugation__mastery">
                            {cell.correct}/{cell.attempts} before
                          </span>
                        )}
                      </li>
                    );
                  })}
              </ul>
            </section>
          ))}

          {!submitted && (
            <Button
              loading={submit.isPending}
              onClick={() =>
                submit.mutate(
                  grid.cells.map((cell) => ({
                    tense: cell.tense,
                    person: cell.person,
                    correct:
                      (entries[`${cell.tense}:${cell.person}`] ?? '').trim().toLowerCase() ===
                      cell.form.toLowerCase(),
                  })),
                )
              }
            >
              Check the grid
            </Button>
          )}

          {submitted && submit.data !== undefined && (
            <>
              <p className="vl-note">{submit.data.accuracy}% this time.</p>
              {submit.data.weakest.length > 0 ? (
                <Button variant="secondary" onClick={() => onNavigate('/review/session')}>
                  Drill my weak cells ({submit.data.weakest.length})
                </Button>
              ) : (
                // Offers the deck rather than an empty trainer.
                <p className="vl-note">
                  Nothing weak left on this verb. Try another, or go back to your deck.
                </p>
              )}
            </>
          )}
        </>
      )}
    </main>
  );
}
